import { Link, useParams } from '@tanstack/react-router';
import { type FormEvent, useState } from 'react';
import { api, call } from '../../api/client';
import { useAction, useDelegation, useMembers, usePendingDecisions, usePriorities, usePriorityRequest } from '../../api/queries';
import { useHousehold } from '../../api/session';
import { Badge, Button, Card, EmptyState, ErrorText, Field, Input, PageHeader, Select, Spinner, Textarea } from '../../components/ui';
import { timeAgo } from '../../lib/format';

function useNames() {
  const { memberId } = useHousehold();
  const members = useMembers().data ?? [];
  return (id: string | null | undefined) => (id === memberId ? 'You' : (members.find((m) => m.id === id)?.displayName ?? 'Someone'));
}

type Item = { kind: string; id: string; taskId?: string | null; title: string; childMemberId?: string; fromMemberId?: string; message?: string | null; proposed?: string; from?: string };

export function DecisionsPage() {
  const q = usePendingDecisions();
  const name = useNames();
  if (!q.data) return <Spinner />;
  const awaiting = q.data.awaitingMe as Item[];
  const outgoing = q.data.outgoing as Item[];
  const describe = (i: Item) => {
    switch (i.kind) {
      case 'TRANSFER_INCOMING': return `${name(i.fromMemberId)} asked you to take “${i.title}”${i.message ? ` — “${i.message}”` : ''}`;
      case 'TRANSFER_OUTGOING': return `You asked to hand off “${i.title}”`;
      case 'DELEGATION_APPROVAL': return `Approve “${i.title}” for ${name(i.childMemberId)}?`;
      case 'DELEGATION_DISPUTED': return `Your proposal for ${name(i.childMemberId)} (“${i.title}”) was questioned`;
      case 'DELEGATION_OUTGOING': return `“${i.title}” for ${name(i.childMemberId)} — waiting for approval`;
      case 'PRIORITY_RESPONSE': return `Priority of “${i.title}”: ${i.from?.toLowerCase()} → ${i.proposed?.toLowerCase()} proposed`;
      default: return `Priority discussion about “${i.title}” — waiting for a reply`;
    }
  };
  const link = (i: Item) =>
    i.kind.startsWith('DELEGATION')
      ? { to: '/decisions/delegations/$proposalId' as const, params: { proposalId: i.id } }
      : i.kind.startsWith('PRIORITY')
        ? { to: '/decisions/priority/$requestId' as const, params: { requestId: i.id } }
        : { to: '/tasks/$taskId' as const, params: { taskId: i.taskId ?? '' } };
  const list = (items: Item[]) => (
    <ul className="space-y-2">
      {items.map((i) => (
        <li key={`${i.kind}-${i.id}`}>
          <Link {...link(i)}><Card className="text-sm hover:bg-[var(--surface-2)]">{describe(i)}</Card></Link>
        </li>
      ))}
    </ul>
  );
  return (
    <>
      <PageHeader title="Pending Decisions" subtitle="Requests from your partner, and the ones you're waiting on." />
      {awaiting.length ? list(awaiting) : <EmptyState title="Nothing needs your decision" />}
      {outgoing.length ? (
        <section className="mt-8">
          <h2 className="mb-2 text-sm font-medium text-[var(--muted)]">Waiting on someone else</h2>
          {list(outgoing)}
        </section>
      ) : null}
    </>
  );
}

export function DelegationPage() {
  const { proposalId } = useParams({ from: '/app/decisions/delegations/$proposalId' });
  const { familyId, memberId } = useHousehold();
  const q = useDelegation(proposalId);
  const name = useNames();
  const children = (useMembers().data ?? []).filter((m) => m.role === 'CHILD');
  const [mode, setMode] = useState<null | 'disagree' | 'decline' | 'revise'>(null);
  const p = q.data;
  const path = { params: { path: { familyId, proposalId } } };
  const act = useAction(async ({ action, f }: { action: string; f?: FormData }) => {
    const revisionNo = p!.currentRevisionNo;
    switch (action) {
      case 'approve': return call(api.POST('/api/v1/families/{familyId}/child-delegations/{proposalId}/approve', { ...path, body: { revisionNo } }));
      case 'withdraw': return call(api.POST('/api/v1/families/{familyId}/child-delegations/{proposalId}/withdraw', path));
      case 'disagree': return call(api.POST('/api/v1/families/{familyId}/child-delegations/{proposalId}/disagree', { ...path, body: { revisionNo, reason: String(f!.get('reason')) } }));
      case 'decline': return call(api.POST('/api/v1/families/{familyId}/child-delegations/{proposalId}/decline', { ...path, body: { revisionNo, reason: String(f!.get('reason')) } }));
      case 'comment': return call(api.POST('/api/v1/families/{familyId}/child-delegations/{proposalId}/comments', { ...path, body: { revisionNo, body: String(f!.get('body')) } }));
      default:
        return call(
          api.POST('/api/v1/families/{familyId}/child-delegations/{proposalId}/revise', {
            ...path,
            body: {
              basedOnRevisionNo: revisionNo,
              childMemberId: String(f!.get('child')),
              title: String(f!.get('title')),
              description: (f!.get('description') as string) || null,
              points: f!.get('points') ? Number(f!.get('points')) : null,
              note: (f!.get('note') as string) || undefined,
            },
          }),
        );
    }
  });
  if (!p) return <Spinner />;
  const current = p.revisions.at(-1)!;
  const open = p.status === 'AWAITING_APPROVAL' || p.status === 'DISPUTED';
  const iAmAuthor = current.authorMemberId === memberId;
  const iMustApprove = p.requiredApprovers.some((a) => a.memberId === memberId && !a.approved);
  const canRevise = open && (p.status === 'DISPUTED' || iAmAuthor);
  const submit = (action: string) => (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    act.mutate({ action, f: new FormData(e.currentTarget) }, { onSuccess: () => setMode(null) });
  };

  return (
    <>
      <PageHeader
        title={`“${p.subjectTitle}” for ${name(current.terms.childMemberId)}`}
        subtitle={
          <span className="flex flex-wrap gap-1.5">
            <Badge tone={p.status === 'APPROVED' ? 'accent' : p.status === 'DISPUTED' ? 'warn' : 'neutral'}>{p.status.replace('_', ' ').toLowerCase()}</Badge>
            <Badge>{p.scope === 'SERIES' ? 'Every time (recurring)' : 'Just this task'}</Badge>
            {p.amendsChildAssignmentId ? <Badge tone="info">Change to an existing arrangement</Badge> : null}
          </span>
        }
      />
      <p className="mb-4 text-xs text-[var(--muted)]">Only adults can see this discussion. The child only sees the chore once it's approved.</p>

      <ol className="space-y-3">
        {p.revisions.map((r) => (
          <li key={r.revisionNo}>
            <Card className={r.revisionNo === p.currentRevisionNo ? 'border-[var(--accent)]' : 'opacity-75'}>
              <p className="text-xs text-[var(--muted)]">Version {r.revisionNo} · {name(r.authorMemberId)} · {timeAgo(r.createdAt)}</p>
              <p className="mt-1 font-medium">{r.terms.title} → {name(r.terms.childMemberId)}</p>
              {r.terms.description ? <p className="mt-1 whitespace-pre-wrap text-sm">{r.terms.description}</p> : null}
              {r.terms.points ? <p className="mt-1 text-sm">⭐ {r.terms.points} points</p> : null}
              {r.note ? <p className="mt-2 text-sm italic text-[var(--muted)]">“{r.note}”</p> : null}
              {r.responses.map((x, i) => (
                <p key={i} className="mt-2 rounded-lg bg-[var(--surface-2)] px-3 py-2 text-sm">
                  <strong>{name(x.responderMemberId)}</strong> {x.kind === 'APPROVE' ? 'approved' : x.kind === 'DISAGREE' ? 'disagreed' : x.kind === 'DECLINE' ? 'declined' : 'commented'}
                  {x.body ? `: “${x.body}”` : ''}
                </p>
              ))}
            </Card>
          </li>
        ))}
      </ol>

      {open ? (
        <div className="mt-5 space-y-3">
          <div className="flex flex-wrap gap-2">
            {iMustApprove ? <Button busy={act.isPending} onClick={() => act.mutate({ action: 'approve' })}>Approve</Button> : null}
            {iMustApprove && p.status === 'AWAITING_APPROVAL' ? <Button variant="secondary" onClick={() => setMode('disagree')}>Disagree…</Button> : null}
            {canRevise ? <Button variant="secondary" onClick={() => setMode('revise')}>Suggest a change…</Button> : null}
            {iMustApprove ? <Button variant="ghost" onClick={() => setMode('decline')}>Decline…</Button> : null}
            {p.proposerMemberId === memberId ? <Button variant="ghost" busy={act.isPending} onClick={() => act.mutate({ action: 'withdraw' })}>Withdraw</Button> : null}
          </div>
          {mode === 'disagree' || mode === 'decline' ? (
            <form className="space-y-2" onSubmit={submit(mode)}>
              <Field label={mode === 'disagree' ? 'What worries you?' : 'Why not?'}>{(id) => <Textarea id={id} name="reason" required maxLength={1000} />}</Field>
              <Button type="submit" variant={mode === 'decline' ? 'danger' : 'primary'} busy={act.isPending}>{mode === 'disagree' ? 'Send' : 'Decline proposal'}</Button>
            </form>
          ) : null}
          {mode === 'revise' ? (
            <form className="space-y-3" onSubmit={submit('revise')}>
              <Field label="Child">{(id) => <Select id={id} name="child" defaultValue={current.terms.childMemberId}>{children.map((c) => <option key={c.id} value={c.id}>{c.displayName}</option>)}</Select>}</Field>
              <Field label="What the child is asked to do">{(id) => <Input id={id} name="title" required maxLength={200} defaultValue={current.terms.title} />}</Field>
              <Field label="Instructions">{(id) => <Textarea id={id} name="description" maxLength={4000} defaultValue={current.terms.description ?? ''} />}</Field>
              <Field label="Points">{(id) => <Input id={id} name="points" type="number" min={0} max={1000} defaultValue={current.terms.points ?? ''} />}</Field>
              <Field label="Note">{(id) => <Textarea id={id} name="note" maxLength={1000} placeholder="Instead, let's have them empty only the bedroom bin." />}</Field>
              <Button type="submit" busy={act.isPending}>Send revised version</Button>
            </form>
          ) : null}
          <form className="flex gap-2" onSubmit={(e) => { submit('comment')(e); e.currentTarget.reset(); }}>
            <Input name="body" placeholder="Reply to the discussion" maxLength={1000} required />
            <Button type="submit" variant="secondary" busy={act.isPending}>Reply</Button>
          </form>
          <ErrorText error={act.error} />
        </div>
      ) : null}
    </>
  );
}

export function PriorityPage() {
  const { requestId } = useParams({ from: '/app/decisions/priority/$requestId' });
  const { familyId, memberId } = useHousehold();
  const q = usePriorityRequest(requestId);
  const priorities = usePriorities().data ?? [];
  const name = useNames();
  const [mode, setMode] = useState<null | 'counter' | 'decline'>(null);
  const r = q.data;
  const path = { params: { path: { familyId, requestId } } };
  const act = useAction(async ({ action, f }: { action: string; f?: FormData }) => {
    const offerNo = r!.currentOfferNo;
    if (action === 'accept') return call(api.POST('/api/v1/families/{familyId}/priority-change-requests/{requestId}/accept', { ...path, body: { offerNo } }));
    if (action === 'withdraw') return call(api.POST('/api/v1/families/{familyId}/priority-change-requests/{requestId}/withdraw', path));
    if (action === 'decline') return call(api.POST('/api/v1/families/{familyId}/priority-change-requests/{requestId}/decline', { ...path, body: { offerNo, reason: String(f!.get('reason')) } }));
    return call(api.POST('/api/v1/families/{familyId}/priority-change-requests/{requestId}/counter', { ...path, body: { offerNo, proposedPriority: String(f!.get('priority')), reason: String(f!.get('reason')) } }));
  });
  if (!r) return <Spinner />;
  const label = (code: string) => priorities.find((p) => p.code === code)?.label ?? code;
  const myTurn = r.status === 'AWAITING_RESPONSE' && r.awaitingMemberId === memberId;
  const latest = r.offers.at(-1)!;
  const submit = (action: string) => (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    act.mutate({ action, f: new FormData(e.currentTarget) }, { onSuccess: () => setMode(null) });
  };
  return (
    <>
      <PageHeader
        title={`Priority of “${r.taskTitle}”`}
        subtitle={<>Official priority: <strong>{label(r.officialPriority)}</strong> · {r.status === 'AWAITING_RESPONSE' ? `waiting for ${name(r.awaitingMemberId)}` : r.status.toLowerCase()}</>}
      />
      <ol className="space-y-2">
        {r.offers.map((o) => (
          <li key={o.offerNo}>
            <Card className="text-sm">
              <p className="text-xs text-[var(--muted)]">{name(o.authorMemberId)} · {timeAgo(o.createdAt)}</p>
              <p className="mt-1">Suggests <strong>{label(o.proposedPriority)}</strong>: “{o.reason}”</p>
            </Card>
          </li>
        ))}
      </ol>
      {r.resolutionNote ? <p className="mt-3 text-sm text-[var(--muted)]">“{r.resolutionNote}”</p> : null}
      {myTurn ? (
        <div className="mt-5 space-y-3">
          <div className="flex flex-wrap gap-2">
            <Button busy={act.isPending} onClick={() => act.mutate({ action: 'accept' })}>Agree: {label(latest.proposedPriority)}</Button>
            <Button variant="secondary" onClick={() => setMode('counter')}>Suggest something else…</Button>
            <Button variant="ghost" onClick={() => setMode('decline')}>Keep {label(r.officialPriority)}…</Button>
          </div>
          {mode === 'counter' ? (
            <form className="space-y-2" onSubmit={submit('counter')}>
              <Field label="Your suggestion">{(id) => <Select id={id} name="priority">{priorities.filter((p) => p.code !== r.baselinePriority && p.code !== latest.proposedPriority).map((p) => <option key={p.code} value={p.code}>{p.label}</option>)}</Select>}</Field>
              <Field label="Why?">{(id) => <Textarea id={id} name="reason" required maxLength={1000} />}</Field>
              <Button type="submit" busy={act.isPending}>Send</Button>
            </form>
          ) : null}
          {mode === 'decline' ? (
            <form className="space-y-2" onSubmit={submit('decline')}>
              <Field label="Why?">{(id) => <Textarea id={id} name="reason" required maxLength={1000} placeholder="I checked it this morning — it's only dripping into the sink." />}</Field>
              <Button type="submit" busy={act.isPending}>Keep current priority</Button>
            </form>
          ) : null}
        </div>
      ) : r.status === 'AWAITING_RESPONSE' && r.requestedByMemberId === memberId ? (
        <Button className="mt-4" variant="ghost" busy={act.isPending} onClick={() => act.mutate({ action: 'withdraw' })}>Withdraw suggestion</Button>
      ) : null}
      <ErrorText error={act.error} />
      <Link to="/tasks/$taskId" params={{ taskId: r.taskId }} className="mt-6 block text-sm text-[var(--accent)] hover:underline">Open the task</Link>
    </>
  );
}
